'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  describeLoginError,
  login as loginRequest,
  resendOtp as resendOtpRequest,
  verifyOtp as verifyOtpRequest,
  type AuthStep,
  type Credentials,
  type OtpChallenge,
  type Session,
} from '@/services/authApi';
import { handleUnauthorized } from '@/lib/auth/tokenRefresh';
import { setUnauthorizedHandler } from '@/services/httpClient';
import { readStoredSession, SESSION_CHANGE_EVENT, writeStoredSession } from '@/services/sessionStorage';

interface AuthContextValue {
  session: Session | null;
  /** False until sessionStorage has been read — do not redirect while this is false. */
  isReady: boolean;
  isAuthenticated: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  /** Set while the account waits for its SMS code (2FA accounts such as admins). */
  otpChallenge: OtpChallenge | null;
  /** True when signed in; false on failure or when an SMS code is now required. */
  signIn: (credentials: Credentials) => Promise<boolean>;
  verifyOtp: (code: string) => Promise<boolean>;
  resendOtp: () => Promise<boolean>;
  /** Leave the code step and go back to email/password. */
  cancelOtp: () => void;
  signOut: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Same limit as the back office: after this many wrong codes, start over. */
export const OTP_MAX_WRONG_CODES = 3;

function hasActiveSession(session: Session | null): boolean {
  return Boolean(session?.token);
}

/**
 * Session state for the operator login. Deliberately stored in
 * `sessionStorage`, not `localStorage`: these devices sit on shop counters and
 * shared back-office machines, so a closed tab should end the session.
 *
 * Mount once near the root so navigating login ↔ dashboard does not remount
 * and briefly treat a valid session as logged-out (redirect loop).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [otpChallenge, setOtpChallenge] = useState<OtpChallenge | null>(null);
  const [wrongCodes, setWrongCodes] = useState(0);

  useEffect(() => {
    const syncFromStorage = () => {
      setSession(readStoredSession());
      setIsReady(true);
    };

    syncFromStorage();
    window.addEventListener(SESSION_CHANGE_EVENT, syncFromStorage);
    // Expired access tokens are refreshed transparently; a dead session clears storage → login.
    setUnauthorizedHandler(handleUnauthorized);
    return () => {
      window.removeEventListener(SESSION_CHANGE_EVENT, syncFromStorage);
      setUnauthorizedHandler(null);
    };
  }, []);

  /** Applies a login/verify result; returns true once a session exists. */
  const applyStep = useCallback((step: AuthStep) => {
    if (step.kind === 'otp') {
      setOtpChallenge(step.challenge);
      setWrongCodes(0);
      return false;
    }
    setOtpChallenge(null);
    setSession(step.session);
    writeStoredSession(step.session);
    return true;
  }, []);

  const runAuthRequest = useCallback(async (task: () => Promise<boolean>, onError?: (error: unknown) => void) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      return await task();
    } catch (error) {
      setErrorMessage(describeLoginError(error));
      onError?.(error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const signIn = useCallback(
    (credentials: Credentials) => runAuthRequest(async () => applyStep(await loginRequest(credentials))),
    [applyStep, runAuthRequest],
  );

  const verifyOtp = useCallback(
    (code: string) => {
      const challenge = otpChallenge;
      if (!challenge) return Promise.resolve(false);
      return runAuthRequest(
        async () => applyStep(await verifyOtpRequest(challenge.otpToken, code)),
        () => {
          const attempts = wrongCodes + 1;
          setWrongCodes(attempts);
          if (attempts >= OTP_MAX_WRONG_CODES) {
            setOtpChallenge(null);
            setErrorMessage('Too many incorrect codes. Please sign in again.');
          }
        },
      );
    },
    [otpChallenge, wrongCodes, applyStep, runAuthRequest],
  );

  const resendOtp = useCallback(() => {
    const challenge = otpChallenge;
    if (!challenge) return Promise.resolve(false);
    return runAuthRequest(async () => {
      const next = await resendOtpRequest(challenge.otpToken);
      setOtpChallenge({ ...challenge, ...next, sentTo: next.sentTo ?? challenge.sentTo });
      return true;
    });
  }, [otpChallenge, runAuthRequest]);

  const cancelOtp = useCallback(() => {
    setOtpChallenge(null);
    setWrongCodes(0);
    setErrorMessage(null);
  }, []);

  const signOut = useCallback(() => {
    setSession(null);
    writeStoredSession(null);
  }, []);

  const clearError = useCallback(() => setErrorMessage(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isReady,
      isAuthenticated: hasActiveSession(session),
      isSubmitting,
      errorMessage,
      otpChallenge,
      signIn,
      verifyOtp,
      resendOtp,
      cancelOtp,
      signOut,
      clearError,
    }),
    [session, isReady, isSubmitting, errorMessage, otpChallenge, signIn, verifyOtp, resendOtp, cancelOtp, signOut, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
